import sha1 from '/fp/sha1.js';
import { picassoPaint, x64Add, x64hash128 } from '/fp/picasso.js';
import { pushCollectIncident } from '/fp/collect-incidents.js';
const knownTextMetrics = {
  // Blink
  169.9375: 'Linux', // Chrome OS
  169.4443359375: 'Linux', // Chrome OS/CloudReady
  164.6962890625: 'Linux', // Fedora/Ubuntu
  170.4443359375: 'Linux', // Fedora/Ubuntu (onscreen)
  173.9521484375: 'Windows', // Windows 10
  163.5068359375: 'Windows', // Windows 7-8.1
  156.5068359375: 'Windows', // Windows 7-8.1 (onscreen)
  159.87109375: 'Android', // Android 8-11
  161.93359375: 'Android', // Android 9/Chrome OS
  160.021484375: 'Android', // Android 5-7
  170.462890625: 'Apple', // Mac Yosemite-Big Sur
  172.462890625: 'Apple', // Mac Mojave
  162.462890625: 'Apple', // Mac Yosemite-Big Sur (onscreen)
  // Gecko (onscreen)
  163.48333384195962: 'Linux', // Fedora/Ubuntu
  163: 'Linux', // Ubuntu/Tor Browser
  170.38938852945964: 'Windows', // Windows 10
  159.9560546875: 'Windows', // Windows 7-8
  165.9560546875: 'Windows', // Tor Browser
  173.43938852945962: 'Apple', // Mac Yosemite-Big Sur (+Tor Browser)
  159.70088922409784: 'Android', // Android 11
  159.71331355882728: 'Android', // Android 11
  159.59375152587893: 'Android', // Android 11
  159.75551515467026: 'Android', // Android 10
  161.7770797729492: 'Android', // Android 9
  159.86563110351562: 'Android', // Android 8
  // WebKit (onscreen)
  172.955078125: 'Apple', // Mac, CriOS
};

const compute_dom_rect = async (incidents) => {
  let div = null;
  try {
    if (!document.body) return null;

    div = document.createElement('div');
    div.id = 'rectTest';
    div.style.backgroundColor = 'skyblue';
    div.style.top = 0;
    div.style.left = 0;
    div.style.width = '100px';
    div.style.height = '100px';
    div.style.transform = 'rotate(45deg)';
    div.style.padding = 0;
    div.style.opacity = 0;
    div.style.zIndex = -20;
    div.style.position = 'fixed';
    document.body.append(div);

    const el = document.getElementById('rectTest');
    if (!el) return null;
    const details = JSON.parse(JSON.stringify(el.getBoundingClientRect()));
    const hash1 = sha1(Object.values(details).join(','));

    return { hash: hash1, details: details };
  } catch (e) {
    if (Array.isArray(incidents)) pushCollectIncident(incidents, { source: 'canvas', phase: 'domRect', error: e });
    return null;
  } finally {
    // Always remove the probe node — otherwise a throw between append and remove leaks
    // div#rectTest into the DOM and repeated collects accumulate duplicate ids.
    if (div) div.remove();
  }
};

async function compute_gpu_canvas(incidents) {
  const params = {
    area: { width: 256, height: 256 },
    offsetParameter: 2001000001,
    fontSizeFactor: 1.5,
    multiplier: 15000,
    maxShadowBlur: 50,
  };

  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl', { desynchronized: true, preserveDrawingBuffer: true });
    if (!context) return null;
    picassoPaint(canvas, context, 5, 42, params);
    const hash = x64hash128(canvas.toDataURL(), 42);
    try {
      const ext = context.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    } catch (e) {
      /* ignore */
    }
    return { hash: hash, details: params };
  } catch (e) {
    if (Array.isArray(incidents)) pushCollectIncident(incidents, { source: 'canvas', phase: 'gpuCanvas', error: e });
    return null;
  }
}

async function compute_cpu_canvas(incidents) {
  const params = {
    area: { width: 256, height: 256 },
    offsetParameter: 2001000001,
    fontSizeFactor: 1.5,
    multiplier: 15000,
    maxShadowBlur: 0,
  };
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    picassoPaint(canvas, context, 5, 42, params);
    const hash = x64hash128(canvas.toDataURL(), 42);
    return { hash: hash, details: params };
  } catch (e) {
    if (Array.isArray(incidents)) pushCollectIncident(incidents, { source: 'canvas', phase: 'cpuCanvas', error: e });
    return null;
  }
}

async function compute_font_canvas(incidents) {
  // if (typeof OffscreenCanvas == 'undefined') return null;

  try {
    // let canvasOffscreen2d = new OffscreenCanvas(186, 30)
    let canvasOffscreen2d = document.createElement('canvas');
    canvasOffscreen2d.width = 186;
    canvasOffscreen2d.height = 30;
    const context2d = canvasOffscreen2d.getContext('2d');
    canvasOffscreen2d.width = 186;
    canvasOffscreen2d.height = 30;
    const str = `😃🙌🧠🦄🐉🌊🍧🏄‍♀️🌠🔮`;
    context2d.font = '14px Arial';
    context2d.fillText(str, 0, 20);
    context2d.fillStyle = 'rgba(0, 0, 0, 0)';
    context2d.fillRect(0, 0, 186, 30);

    const { actualBoundingBoxRight: systemActualBoundingBoxRight, width: systemWidth } =
      context2d.measureText('😃!@#$%^&*') || {};
    const textMetricsSystemSum = (systemActualBoundingBoxRight || 0) + (systemWidth || 0) || undefined;
    const textMetricsSystemClass = knownTextMetrics[textMetricsSystemSum];
    const hash = sha1(String(systemWidth));

    const details = {
      measurement: systemWidth,
      detected_os: textMetricsSystemClass,
    };

    return {
      hash: hash,
      details: details,
    };
  } catch (err) {
    if (Array.isArray(incidents)) pushCollectIncident(incidents, { source: 'canvas', phase: 'fontCanvas', error: err });
    return null;
  }
}

export { compute_gpu_canvas, compute_cpu_canvas, compute_font_canvas, compute_dom_rect };
